import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import ListingItem from '../components/ListingItem';
import StatusItem from '../components/StatusItem';
import Spinner from '../components/Spinner';
import AcquireBanner from '../components/AcquireBanner';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';

export default function Home() {
  const auth = getAuth();
  const [listings, setListings] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const statusListRef = useRef(null);
  const mainContentRef = useRef(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const listingsRef = collection(db, 'listings');
        const listingsQuery = query(listingsRef, orderBy('timestamp', 'desc'));
        const listingsSnap = await getDocs(listingsQuery);

        const listingList = listingsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const shuffledListings = [...listingList].sort(() => Math.random() - 0.5);
        setListings(shuffledListings);

        const statusRef = collection(db, 'status');
        const statusQuery = query(statusRef, orderBy('timestamp', 'desc'));
        const statusSnap = await getDocs(statusQuery);
        const currentTime = new Date();

        const statusList = statusSnap.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            viewedBy: doc.data().viewedBy || []
          }))
          .filter(status => {
            const timestamp = status.timestamp?.toDate();
            if (!timestamp) return false;
            const timeDiff = (currentTime - timestamp) / (1000 * 60 * 60);
            return timeDiff <= 24;
          });

        const sortedStatuses = statusList.sort((a, b) => {
          const aViewed = a.viewedBy.includes(auth.currentUser?.uid);
          const bViewed = b.viewedBy.includes(auth.currentUser?.uid);
          if (aViewed && !bViewed) return 1;
          if (!aViewed && bViewed) return -1;
          return b.timestamp.toDate() - a.timestamp.toDate();
        });

        setStatuses(sortedStatuses);
      } catch (error) {
        toast.error('Failed to load content');
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [auth.currentUser]);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrollTop = window.scrollY;
      const progress = (scrollTop / documentHeight) * 100;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollLeft = () => {
    if (statusListRef.current) {
      statusListRef.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (statusListRef.current) {
      statusListRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <main className="min-h-screen bg-black px-4 sm:px-8 lg:px-16 relative" ref={mainContentRef}>
      {/* VERTICAL SCROLL BAR - REPLACES THE BUTTONS */}
      <div className="fixed right-0 top-0 h-full w-8 flex items-center justify-center z-50">
        <div className="relative h-3/4 w-2">
          {/* Track */}
          <div className="absolute inset-0 bg-gray-800/30 rounded-full backdrop-blur-sm" />
          
          {/* Thumb - gradient with scroll progress */}
          <div 
            className="absolute w-2 rounded-full bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 transition-all duration-150"
            style={{
              height: `${scrollProgress}%`,
              bottom: 0,
              boxShadow: '0 0 15px rgba(59,130,246,0.5), 0 0 30px rgba(168,85,247,0.3)'
            }}
          />
        </div>
      </div>

      {/* STATUS FIRST */}
      {statuses.length > 0 && (
        <>
          <h2 className="text-gray-400 text-lg font-medium pt-4">Status</h2>

          <div className="relative">
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white z-10"
            >
              <FaChevronLeft size={22} />
            </button>

            <div
              ref={statusListRef}
              className="flex overflow-x-auto py-2 px-8 scrollbar-hide"
            >
              {statuses.map(status => (
                <StatusItem key={status.id} status={status} />
              ))}
            </div>

            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white z-10"
            >
              <FaChevronRight size={22} />
            </button>
          </div>

          <div className="border-t border-gray-800 mt-2" />
        </>
      )}

      {/* GLASS WELCOME BOARD - FIXED GRADIENT */}
      <section className="w-full flex justify-center mt-6">
        <div className="relative w-full rounded-2xl overflow-hidden border border-white/20 shadow-2xl">
          {/* Base gradient layers */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/30 via-transparent to-pink-600/30" />
          <div className="absolute inset-0 bg-gradient-to-tl from-blue-600/30 via-transparent to-pink-600/30" />
          <div className="absolute inset-0 bg-black/40 backdrop-blur-xl" />
          
          {/* Radial gradients for depth */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_30%,_rgba(59,130,246,0.35),_transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_70%,_rgba(236,72,153,0.35),_transparent_50%)]" />

          {/* Content */}
          <div className="relative px-6 sm:px-12 py-12 sm:py-16 text-center z-10">
            <h1 className="text-3xl sm:text-5xl font-bold text-white mb-3">
              Welcome to SourceCodey.
            </h1>

            <h2 className="text-xl sm:text-2xl font-semibold text-blue-300 mb-8">
              The #1 Digital Products Marketplace for Future Founders
            </h2>

            <div className="mb-8 flex justify-center">
              <div className="w-full sm:w-auto sm:min-w-[420px]">
                <AcquireBanner />
              </div>
            </div>

            <div className="space-y-1 text-gray-300 text-lg sm:text-xl">
              <p>Where ideas become assets.</p>
              <p>Where creativity becomes income.</p>
              <p>Where builders become brands.</p>
            </div>

            <p className="mt-8 text-lg sm:text-xl font-semibold 
                          bg-gradient-to-r from-blue-400 to-pink-400 
                          bg-clip-text text-transparent">
              You're at the Source. Keep floating, keep sailing.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURED TITLE */}
      <h2 className="text-gray-400 text-lg font-medium mt-4">
        Featured listings
      </h2>

      {/* LISTINGS */}
      <div className="mt-2">
        <div className="movie-masonry">
          {listings.length > 0 ? (
            listings.map(listing => (
              <div key={listing.id} className="masonry-item">
                <ListingItem listing={listing} />
              </div>
            ))
          ) : (
            <p className="text-gray-400 text-center w-full">
              No listings fetched, check your internet connection!
            </p>
          )}
        </div>
      </div>
    </main>
  );
}

/* Inject styles */
const styles = `
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.movie-masonry {
  column-count: 2;
  column-gap: 6px;
}

@media (min-width: 640px) {
  .movie-masonry {
    column-count: 3;
  }
}

@media (min-width: 1024px) {
  .movie-masonry {
    column-count: 4;
  }
}

.masonry-item {
  break-inside: avoid;
  margin-bottom: 6px;
}
`;

if (!document.querySelector('#home-styles')) {
  const styleSheet = document.createElement("style");
  styleSheet.id = "home-styles";
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}